import settings from "../settings";
import axios from "axios";

let filters = {
    periodRange: [],
    regions: ["ee", "eu", "noneu"],
    productTypes: ["product"],
    categoryIds: [],
    brandIds: [],
    productCodes: [],
    netoWeights: null,
    countries: null,
    clients: [],
    chains: [],
    shops: [],
    salesmen: null,
    infoProviders: [],
    shopTypes: [],
};

let defaultChartOptions = {
    boost: {
        enabled: true,
        useGPUTranslations: true,
        usePreallocated: true,
    },
    chart: {
        type: "column",
        marginTop: 50,
    },
    title: {
        text: "",
    },
    xAxis: {
        categories: [],
    },
    yAxis: [
        {
            title: { text: "" },
            min: 0,
            opposite: false,
            stackLabels: {
                enabled: true,
                style: {
                    fontWeight: "bold",
                    color: "gray",
                },
                allowOverlap: true,
            },
        },
        {
            title: { text: "Marginaal" },
            min: 0,
            softMax: 100,
            opposite: true,
        },
        {
            title: { text: "Hind €" },
            min: 0,
            opposite: true,
        },
    ],
    plotOptions: {
        column: {
            stacking: "normal",
        },
    },
    series: [
        {
            name: "Käive",
            data: [],
            stack: "turnover",
            color: "rgba(3, 4, 94, 1)",
        },
        {
            name: "Kampaania käive",
            data: [],
            stack: "turnover",
            color: "rgba(255, 214, 10, 1)",
            id: "campaign",
        },
        {
            name: "Käive reeglid maha",
            data: [],
            stack: "turnover_rules",
            color: "rgba(0, 119, 182, 1)",
        },
        {
            name: "Kampaania käive reeglid maha",
            data: [],
            stack: "turnover_rules",
            color: "rgba(255, 214, 10, 1)",
        },
        {
            name: "Kogus",
            data: [],
            stack: "quantity",
            color: "rgba(0, 180, 216, 1)",
        },
        {
            name: "Kampaania kogus",
            data: [],
            stack: "quantity",
            color: "rgba(255, 214, 10, 1)",
        },
    ],
};

let calculatedSales = {
    'products': [],
    'categories': [],
    'brands': [],
    'netoWeights': [],
    'clients': [],
    'chains': [],
    'shops': [],
    'shopType': [],
    'salesmen': [],
    'countries': [],
    'infoProviders': [],
    'productTypes': [],
};

let initialState = {
    groupByPeriod: "year-month",
    filters: { ...filters },
    chartOptions: { ...defaultChartOptions },
    optionsCache: {
        products: [],
        categories: [],
        brands: [],
        netoWeights: [],
        countries: [],
        clients: [],
        infoProviders: [],
        chains: [],
        shops: [],
        salesmen: [],
        shopTypes: [],
        productTypes: []
    },
    optionsCacheValid: false,
    headers: [
        {text: "Client", value: "client_name", align: "left"},
        {text: "Code", value: "client_code", align: "left"},
        {text: "Turnover", value: "turnover", align: "left"},
        {text: "Turnover rules", value: "turnover_rules", align: "left"},
        {text: "Quantity", value: "quantity", align: "left"},
        {text: "Rules %", value: "rules_percent", align: "left"},
        // {text: "Neto weight", value: "neto_weight", align: "left"},
        {text: "Turnover CY", value: "turnover_cy", align: "left"},
        {text: "Turnover CY-1", value: "turnover_cy_1", align: "left"},
        {text: "Difference", value: "turnover_cy1cy2_diff", align: "left"},
        {text: "Turnover CY-2", value: "turnover_cy_2", align: "left"},
    ],
    productHeaders: [
        {text: "Product", value: "product_name", align: "left"},
        {text: "Code", value: "product_code", align: "left"},
        {text: "Turnover", value: "turnover", align: "left"},
        {text: "Turnover rules", value: "turnover_rules", align: "left"},
        {text: "Quantity", value: "quantity", align: "left"},
        {text: "Rules %", value: "rules_percent", align: "left"},
        // {text: "Neto weight", value: "neto_weight", align: "left"},
        {text: "Turnover CY", value: "turnover_cy", align: "left"},
        {text: "Turnover CY-1", value: "turnover_cy_1", align: "left"},
        {text: "Difference", value: "turnover_cy1cy2_diff", align: "left"},
        {text: "Turnover CY-2", value: "turnover_cy_2", align: "left"},
    ],
    sales: [],
    filtersVisible: true,
    loadingClients: false,
    dataLoading: false,
    showSkeleton: false,
    firstLoadingDone: false,
    errorMsg: null,
    requestError: false,
    datesChanged: true,
    filterDates: {},
    fetchDates: {},
    clientTableData: [],
    productTableData: [],
    exportLoading: false,
    calculatedSales: {...calculatedSales},
    calculateDebounceTimer: null,
    comments: [],
    filterOptionsEnabled: false,  // When true, filter options are calculated (slower but accurate)
    calculatingFilterOptions: false  // When true, shows loading on dropdowns
};

export default {
    namespaced: true,
    state: { ...initialState },
    actions: {
        async fetchData({state, commit, dispatch}) {
            commit("setShowSkeleton", true);
            commit("setFirstLoadingDone", true);
            commit("setDataLoading", true);
            commit("setDatesChanged", false);
            commit("resetChart");
            let filters = { ...state.filters };

            if (state.filterDates?.start && state.filterDates?.end) {
                const startYear = state.filterDates.start.getFullYear();
                const startMonth = (state.filterDates.start.getMonth() + 1).toString().padStart(2, "0");
                const startDay = state.filterDates.start.getDate().toString().padStart(2, "0");
                const endYear = state.filterDates.end.getFullYear();
                const endMonth = (state.filterDates.end.getMonth() + 1).toString().padStart(2, "0");
                const endDay = state.filterDates.end.getDate().toString().padStart(2, "0");
                const dateData = {
                    start: `${startYear}-${startMonth}-${startDay}`,
                    end: `${endYear}-${endMonth}-${endDay}`,
                };
                filters.periodRange = dateData;
                commit("setFetchDates", dateData);
            }

            filters.groupByPeriod = state.groupByPeriod;

            try {
                let result = await axios.post(settings.api_url + "/analysis-data/", filters);

                if (result && result.data && result.data.success === 1) {
                    let data = result.data.sales;
                    commit("setSales", data);
                    commit("setShowSkeleton", false);
                    await dispatch("fetchClientTableData")
                    await dispatch("fetchProductTableData")
                    dispatch('calculateSalesOptions')
                    dispatch('buildOptionsCache')
                } else {
                    let data = result.data;
                    let errorMsg = data.msg || "Error";
                    commit("setErrorMessage", errorMsg);
                    commit("setRequestError", true);
                    commit("setShowSkeleton", true);
                }
            } catch (e) {
                console.log("- ANALYSIS DATA FETCH ERROR -");
                console.log(e);
            } finally {
                commit("setDataLoading", false);
            }
        },
        async fetchClientTableData({state, commit}) {
            const clientCodes = [...new Set(state.sales.map(s => s.client_code).filter(Boolean))];
            if (!clientCodes.length) return;

            try {
                const res = await axios.post(settings.api_url + "/sales-data/", {
                    type: "client",
                    codes: clientCodes
                });
                if (res.data?.success) {
                    commit("setClientTurnovers", res.data.turnovers);
                }
            } catch (e) {
                console.log("Client data fetch error", e);
            }
        },

        async fetchProductTableData({state, commit}) {
            const productIds = [...new Set(state.sales.map(s => s.product_id).filter(Boolean))];
            if (!productIds.length) return;

            try {
                const res = await axios.post(settings.api_url + "/sales-data/", {
                    type: "product",
                    codes: productIds
                });
                if (res.data?.success) {
                    commit("setProductTurnovers", res.data.turnovers);
                }
            } catch (e) {
                console.log("Product data fetch error", e);
            }
        },
        async exportFilteredDataExcel({ state, commit }, { saleIds, importIds }) {
            try {
                commit("setExportLoading", true);
                commit("setErrorMessage", "");
                commit("setRequestError", false);
                let filters = { ...state.filters };

                if (state.filterDates?.start && state.filterDates?.end) {
                    const startYear = state.filterDates.start.getFullYear();
                    const startMonth = (state.filterDates.start.getMonth() + 1).toString().padStart(2, "0");
                    const startDay = state.filterDates.start.getDate().toString().padStart(2, "0");
                    const endYear = state.filterDates.end.getFullYear();
                    const endMonth = (state.filterDates.end.getMonth() + 1).toString().padStart(2, "0");
                    const endDay = state.filterDates.end.getDate().toString().padStart(2, "0");
                    filters.periodRange = {
                        start: `${startYear}-${startMonth}-${startDay}`,
                        end: `${endYear}-${endMonth}-${endDay}`,
                    };
                }

                const data = { saleIds, importIds, filters };

                const resp = await axios.post(`${settings.api_url}/analysis-export/`, data, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    responseType: "blob",
                });

                if (resp && resp.status === 200) {
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "Analysis data.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                } else {
                    commit("setRequestError", true);
                    console.error("Unexpected response:", resp);
                }
            } catch (error) {
                console.error("Export failed:", error);
                commit("setRequestError", true);
            } finally {
                commit("setExportLoading", false);
            }
        },

        generateChartData({state, commit, getters}) {
            commit("resetChart");
            const sales = getters.filteredSales();

            if (!sales || sales.length === 0) {
                return {
                    ...state.chartOptions,
                    series: [],
                };
            }

            const monthNames = [
                "Jaan",
                "Veeb",
                "Mär",
                "Apr",
                "Mai",
                "Juuni",
                "Juuli",
                "Aug",
                "Sep",
                "Okt",
                "Nov",
                "Dets",
            ];
            const groupedData = sales.reduce((acc, sale) => {
                const index = state.groupByPeriod === "year-month" ? sale.group_by_key_month : sale.group_by_key_week;
                if (!acc[index]) {
                    acc[index] = {
                        turnover: 0,
                        campaign_turnover: 0,
                        turnover_rules: 0,
                        campaign_turnover_rules: 0,
                        quantity: 0,
                        campaign_quantity: 0,
                        product_own_turnover: 0,
                        marginal: 0,
                        profit_from_base_price_with_rules_deducted: 0,
                        product_own_price_sum: 0,
                        product_own_price_count: 0,
                        product_list_net_price_sum: 0,
                        product_list_net_price_count: 0,
                        avg_own_price: 0,
                        avg_list_net_price: 0,
                    };
                }
                acc[index].turnover += sale.turnover;
                acc[index].campaign_turnover += sale.campaign_turnover;
                acc[index].turnover_rules += sale.turnover_rules;
                acc[index].campaign_turnover_rules += sale.campaign_turnover_rules;
                acc[index].quantity += sale.quantity;
                acc[index].campaign_quantity += sale.campaign_quantity;
                acc[index].product_own_turnover += sale.product_own_turnover;
                acc[index].profit_from_base_price_with_rules_deducted +=
                    sale.profit_from_base_price_with_rules_deducted;

                // Collect prices for averaging (only if > 0)
                if (sale.product_own_price && sale.product_own_price > 0) {
                    acc[index].product_own_price_sum += sale.product_own_price;
                    acc[index].product_own_price_count += 1;
                }
                if (sale.product_list_net_price && sale.product_list_net_price > 0) {
                    acc[index].product_list_net_price_sum += sale.product_list_net_price;
                    acc[index].product_list_net_price_count += 1;
                }

                return acc;
            }, {});

            Object.keys(groupedData).forEach((index) => {
                const data = groupedData[index];
                const turnoverTotalWithRules = data.turnover_rules + data.campaign_turnover_rules;

                data.marginal =
                    turnoverTotalWithRules !== 0 && data.product_own_turnover !== 0
                        ? ((turnoverTotalWithRules - data.product_own_turnover) / turnoverTotalWithRules) * 100
                        : 0;

                // Calculate average prices
                data.avg_own_price = data.product_own_price_count > 0
                    ? data.product_own_price_sum / data.product_own_price_count
                    : 0;
                data.avg_list_net_price = data.product_list_net_price_count > 0
                    ? data.product_list_net_price_sum / data.product_list_net_price_count
                    : 0;
            });

            const xAxisValues = getters.xAxisValues;

            const xAxisCategories = xAxisValues.map((dateStr) => {
                if (state.groupByPeriod.includes("week")) {
                    const [year, week] = dateStr.split("-");
                    const updatedWeek = week.replace("W", "N");

                    return `${updatedWeek} ${year}`;
                } else {
                    const [year, month] = dateStr.split("-");
                    return `${monthNames[parseInt(month, 10) - 1]} ${year.slice(-2)}`;
                }
            });

            const seriesData = [
                xAxisValues.map((key) => Math.round((groupedData[key]?.turnover || 0) * 100) / 100),
                xAxisValues.map((key) => Math.round((groupedData[key]?.campaign_turnover || 0) * 100) / 100),
                xAxisValues.map((key) => Math.round((groupedData[key]?.turnover_rules || 0) * 100) / 100),
                xAxisValues.map((key) => Math.round((groupedData[key]?.campaign_turnover_rules || 0) * 100) / 100),
                xAxisValues.map((key) => groupedData[key]?.quantity || 0),
                xAxisValues.map((key) => groupedData[key]?.campaign_quantity || 0),
                xAxisValues.map((key) => Math.round((groupedData[key]?.marginal || 0) * 100) / 100),
                xAxisValues.map((key) => Math.round((groupedData[key]?.avg_own_price || 0) * 100) / 100),
                xAxisValues.map((key) => Math.round((groupedData[key]?.avg_list_net_price || 0) * 100) / 100),
            ];
            const d = {
                ...state.chartOptions,
                tooltip: {
                    useHTML: true,
                    backgroundColor: "rgba(33, 33, 33, 1)",
                    borderRadius: 8,
                    borderWidth: 0,
                    formatter: function () {
                        const color = this.color;
                        const seriesName = this.series.name
                        const value = this.y;

                        const formattedValue = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

                        let formattedLabel;
                        if (this.series.name.toLowerCase().includes("marginaal")) {
                            formattedLabel = `${formattedValue} %`;
                        } else if (this.series.name.toLowerCase().includes("kogus")) {
                            formattedLabel = `${formattedValue} kg`;
                        } else if (this.series.name.toLowerCase().includes("omahind") || this.series.name.toLowerCase().includes("hinnakirja")) {
                            formattedLabel = `${formattedValue} €/tk`;
                        } else {
                            formattedLabel = `${formattedValue} €`;
                        }

                        return `
                            <div style="display: flex; align-items: center;">
                                <div style="width: 24px; display: flex; align-items: center; justify-content: center;">
                                    <div style="width: 12px; height: 12px; background-color: ${color}; border-radius: 50%;"></div>
                                </div>
                                <div style="display: flex; flex-direction: column;">
                                    <span style="color: rgba(141, 153, 146, 1); font-size: 11px;">${seriesName}</span>
                                    <span style="color: white; font-size: 14px; font-weight: 600;">${formattedLabel}</span>
                                </div>
                            </div>
                        `;
                    },
                },
                xAxis: {
                    ...state.chartOptions.xAxis,
                    categories: xAxisCategories,
                },
                yAxis: [state.chartOptions.yAxis[0], {...state.chartOptions.yAxis[1], softMax: 100}, state.chartOptions.yAxis[2]],
                series: [
                    ...state.chartOptions.series.map((series, index) => {
                        return {
                            ...series,
                            data: seriesData[index] || [],
                            yAxis: 0,
                        };
                    }),
                    {
                        name: "Marginaal",
                        data: seriesData[6],
                        type: "line",
                        color: "rgba(216, 35, 50, 1)",
                        yAxis: 1,
                        tooltip: {valueSuffix: "%"},
                        dataLabels: {enabled: true, format: "{y}%"},
                        marker: {
                            enabled: true,
                            radius: 6,
                            lineWidth: 2,
                            lineColor: "rgba(216, 35, 50, 1)",
                            fillColor: "#ffffff",
                            symbol: "circle",
                        },
                    },
                    {
                        name: "Toote omahind",
                        data: seriesData[7],
                        type: "line",
                        color: "rgba(76, 175, 80, 1)",
                        yAxis: 2,
                        tooltip: {valueSuffix: " €/tk"},
                        dataLabels: {enabled: true, format: "{y}"},
                        marker: {
                            enabled: true,
                            radius: 5,
                            lineWidth: 2,
                            lineColor: "rgba(76, 175, 80, 1)",
                            fillColor: "#ffffff",
                            symbol: "diamond",
                        },
                    },
                    {
                        name: "Toote hinnakirja hind",
                        data: seriesData[8],
                        type: "line",
                        color: "rgba(156, 39, 176, 1)",
                        yAxis: 2,
                        tooltip: {valueSuffix: " €/tk"},
                        dataLabels: {enabled: true, format: "{y}"},
                        marker: {
                            enabled: true,
                            radius: 5,
                            lineWidth: 2,
                            lineColor: "rgba(156, 39, 176, 1)",
                            fillColor: "#ffffff",
                            symbol: "square",
                        },
                    },
                ],
            };
            commit("setChartData", d)
        },
        generateClientTableData({commit, getters}) {
            const sales = getters.filteredSales();
            if (!sales || sales.length === 0) {
                return [];
            }

            const groupedData = sales.reduce((acc, sale) => {
                const clientId = sale.client_code;

                if (!clientId) return acc;
                if (!acc[clientId]) {
                    acc[clientId] = {
                        client: sale.client_name,
                        chain_name: sale.chain_name,
                        client_name: sale.client_shop_name,
                        client_code: clientId,
                        quantity: 0,
                        turnover: 0,
                        turnover_rules: 0,
                        rules_percent: 0,
                        neto_weight: 0,
                        turnover_cy: 0,
                        turnover_cy_1: 0,
                        turnover_cy_2: 0,
                        turnover_cy1cy2_diff: 0,
                    };
                }

                acc[clientId].quantity += sale.quantity || 0;
                acc[clientId].turnover += sale.turnover || 0;
                acc[clientId].turnover_rules += sale.turnover_rules || 0;
                acc[clientId].neto_weight += sale.product_neto_weight || 0;

                if (!acc[clientId].__turnovers_set) {
                    acc[clientId].turnover_cy = sale.client_turnover_cy || 0;
                    acc[clientId].turnover_cy_1 = sale.client_turnover_cy1 || 0;
                    acc[clientId].turnover_cy_2 = sale.client_turnover_cy2 || 0;
                    acc[clientId].turnover_cy1cy2_diff = acc[clientId].turnover_cy_1 - acc[clientId].turnover_cy_2;
                    acc[clientId].__turnovers_set = true;
                }

                return acc;
            }, {});

            const d = Object.keys(groupedData).map((clientId) => {
                const data = groupedData[clientId];
                return {
                    client_name: data.client_name,
                    client: data.client,
                    chain_name: data.chain_name,
                    client_code: data.client_code,
                    quantity: data.quantity,
                    neto_weight: data.neto_weight.toFixed(2),
                    turnover_cy: data.turnover_cy,
                    turnover_cy_1: data.turnover_cy_1,
                    turnover_cy1cy2_diff: data.turnover_cy1cy2_diff,
                    turnover_cy_2: data.turnover_cy_2,
                    turnover: data.turnover,
                    turnover_rules: data.turnover_rules,
                    rules_percent: data.turnover > 0 ? 100 - ((data.turnover_rules / data.turnover) * 100) : 0,
                };
            });
            commit("setClientTableData", d)
        },
        generateProductTableData({commit, getters}) {
            const sales = getters.filteredSales();
            if (!sales || sales.length === 0) return [];

            const groupedData = sales.reduce((acc, sale) => {
                const articleCode = sale.article_code;

                if (!articleCode) return acc;
                if (!acc[articleCode]) {
                    acc[articleCode] = {
                        product_id: sale.product_id,
                        product_code: articleCode,
                        product_name: sale.product_name,
                        product_category_id: sale.product_category_id,
                        product_brand_id: sale.product_brand_id,
                        quantity: 0,
                        turnover: 0,
                        turnover_rules: 0,
                        rules_percent: 0,
                        neto_weight: 0,
                        turnover_cy: 0,
                        turnover_cy_1: 0,
                        turnover_cy_2: 0,
                        turnover_cy1cy2_diff: 0,
                    };
                }

                acc[articleCode].quantity += sale.quantity || 0;
                acc[articleCode].turnover += sale.turnover || 0;
                acc[articleCode].turnover_rules += sale.turnover_rules || 0;
                acc[articleCode].neto_weight += sale.product_neto_weight || 0;

                if (!acc[articleCode].__turnovers_set) {
                    acc[articleCode].turnover_cy = sale.product_turnover_cy || 0;
                    acc[articleCode].turnover_cy_1 = sale.product_turnover_cy1 || 0;
                    acc[articleCode].turnover_cy_2 = sale.product_turnover_cy2 || 0;
                    acc[articleCode].turnover_cy1cy2_diff = acc[articleCode].turnover_cy_1 - acc[articleCode].turnover_cy_2;
                    acc[articleCode].__turnovers_set = true;
                }

                return acc;
            }, {});

            const d = Object.values(groupedData).map(data => ({
                product_id: data.product_id,
                product_code: data.product_code,
                product_name: data.product_name,
                quantity: data.quantity,
                neto_weight: data.neto_weight.toFixed(2),
                turnover_cy: data.turnover_cy,
                turnover_cy_1: data.turnover_cy_1,
                turnover_cy1cy2_diff: data.turnover_cy1cy2_diff,
                turnover_cy_2: data.turnover_cy_2,
                turnover: data.turnover,
                turnover_rules: data.turnover_rules,
                rules_percent: data.turnover > 0 ? 100 - ((data.turnover_rules / data.turnover) * 100) : 0,
                product_category_id: data.product_category_id,
                product_brand_id: data.product_brand_id,
            }));

            commit("setProductTableData", d)
        },

        calculateSalesOptions({commit, state, getters}) {
            // Only show loading spinner if in filtered mode (slow)
            if (state.filterOptionsEnabled) {
                commit('setCalculatingFilterOptions', true);
            }

            // Use requestAnimationFrame to avoid blocking UI
            requestAnimationFrame(() => {
                let data;

                if (state.filterOptionsEnabled) {
                    // SLOW MODE: Filter options based on active filters (accurate but slower)
                    data = {...calculatedSales};

                    // Define exclude mappings once
                    const excludeMappings = {
                        'categories': ["products", "brands", "categories"],
                        'brands': ["products", "brands"],
                        'clients': ["clients", "shops", "chains"],
                        'chains': ["shops", "chains"]
                    };

                    // Process each key with filtering
                    Object.keys(data).forEach(key => {
                        const excludeKey = excludeMappings[key] || [key];
                        data[key] = getters.filteredSales(excludeKey);
                    });
                } else {
                    // FAST MODE: Filter by all current filters (no per-field exclusions)
                    // This is fast because it's a single pass through the data
                    const filteredData = getters.filteredSales([]);
                    data = {
                        'products': filteredData,
                        'categories': filteredData,
                        'brands': filteredData,
                        'netoWeights': filteredData,
                        'clients': filteredData,
                        'chains': filteredData,
                        'shops': filteredData,
                        'shopType': filteredData,
                        'salesmen': filteredData,
                        'countries': filteredData,
                        'infoProviders': filteredData,
                        'productTypes': filteredData
                    };
                }

                commit("setCalculateSales", data)
                commit('setCalculatingFilterOptions', false)
                commit('invalidateOptionsCache');

                // Rebuild cache after filtering recalculation
                requestAnimationFrame(() => {
                    const cache = {
                        products: getters.productOptions,
                        categories: getters.categoryOptions,
                        brands: getters.brandOptions,
                        netoWeights: getters.netoWeightOptions,
                        countries: getters.countryOptions,
                        clients: getters.clientOptions,
                        infoProviders: getters.infoProviderOptions,
                        chains: getters.chainOptions,
                        shops: getters.shopOptions,
                        salesmen: getters.salesmanOptions,
                        shopTypes: getters.shopTypeOptions,
                        productTypes: getters.productTypeOptions
                    };
                    commit('setOptionsCache', cache);
                });
            });
        },
        buildOptionsCache({commit, getters}) {
            // Build all options at once and cache them
            requestAnimationFrame(() => {
                const cache = {
                    products: getters.productOptions,
                    categories: getters.categoryOptions,
                    brands: getters.brandOptions,
                    netoWeights: getters.netoWeightOptions,
                    countries: getters.countryOptions,
                    clients: getters.clientOptions,
                    infoProviders: getters.infoProviderOptions,
                    chains: getters.chainOptions,
                    shops: getters.shopOptions,
                    salesmen: getters.salesmanOptions,
                    shopTypes: getters.shopTypeOptions,
                    productTypes: getters.productTypeOptions
                };
                commit('setOptionsCache', cache);
            });
        },
        async fetchComments({commit}) {
            try {
                const response = await axios.get(settings.api_url + "/analysis-comment/");
                if (response.data?.success === 1) {
                    commit("setComments", response.data.comments || []);
                }
            } catch (e) {
                console.log("Fetch comments error:", e);
            }
        },
        async sendNewComment({commit}, comment) {
            try {
                const response = await axios.post(settings.api_url + "/analysis-comment/", { comment });
                if (response.data?.success === 1) {
                    commit("setComments", response.data.comments || []);
                    return { success: 1 };
                }
            } catch (e) {
                console.log("Send comment error:", e);
            }
            return { success: 0 };
        },
        async deleteComment({commit}, commentId) {
            try {
                const response = await axios.delete(settings.api_url + "/analysis-comment/", {
                    params: { item: commentId }
                });
                if (response.data?.success === 1) {
                    commit("setComments", response.data.comments || []);
                    return { success: 1 };
                }
            } catch (e) {
                console.log("Delete comment error:", e);
            }
            return { success: 0 };
        }
    },
    mutations: {
        setExportLoading(state, bool) {
            state.exportLoading = bool;
        },
        setFilterOptionsEnabled(state, bool) {
            state.filterOptionsEnabled = bool;
        },
        setCalculatingFilterOptions(state, bool) {
            state.calculatingFilterOptions = bool;
        },
        setGroupByPeriod(state, value) {
            state.groupByPeriod = value;
        },
        updateFilters(state, { filter, value }) {
            state.filters = {
                ...state.filters,
                [filter]: value,
            };

            // Reset filterOptionsEnabled when user changes filters
            // This makes all dropdown options show as active (dark) again
            // User needs to click "Filter dropdown options" button again to see filtered colors
            state.filterOptionsEnabled = false;
        },
        resetFilters(state) {
            Object.assign(state.filters, filters);
        },
        setDatesChanged(state, bool) {
            state.datesChanged = bool;
        },
        toggleFilterVisible(state) {
            state.filtersVisible = !state.filtersVisible;
        },
        setErrorMessage(state, errorMsg) {
            state.errorMsg = errorMsg;
        },
        setRequestError(state, bool) {
            state.requestError = bool;
        },
        setSales(state, sales) {
            state.sales = sales;
            state.optionsCacheValid = false;
        },
        setDataLoading(state, bool) {
            state.dataLoading = bool;
        },
        setShowSkeleton(state, bool) {
            state.showSkeleton = bool;
        },
        setFirstLoadingDone(state, bool) {
            state.firstLoadingDone = bool;
        },
        resetChart(state) {
            state.chartOptions = defaultChartOptions;
        },
        setFetchDates(state, data) {
            state.fetchDates = data;
        },
        setFilterDates(state, data) {
            state.filterDates = data;
        },
        setClientTurnovers(state, turnovers) {
            state.sales.forEach(sale => {
                const clientData = turnovers[sale.client_code];
                if (clientData) {
                    sale.client_turnover_cy = clientData.turnover_cy;
                    sale.client_turnover_cy1 = clientData.turnover_cy_1;
                    sale.client_turnover_cy2 = clientData.turnover_cy_2;
                }
            });
        },
        setProductTurnovers(state, turnovers) {
            state.sales.forEach(sale => {
                const productData = turnovers[sale.product_id];
                if (productData) {
                    sale.product_turnover_cy = productData.turnover_cy;
                    sale.product_turnover_cy1 = productData.turnover_cy_1;
                    sale.product_turnover_cy2 = productData.turnover_cy_2;
                }
            });
        },
        setChartData(state, data) {
            state.chartOptions = data;
        },
        setClientTableData(state, data) {
            state.clientTableData = data;
        },
        setProductTableData(state, data) {
            state.productTableData = data;
        },
        resetView(state) {
            Object.assign(state, initialState)
        },
        setCalculateSales(state, data) {
            state.calculatedSales = {...data}
        },
        setOptionsCache(state, cache) {
            state.optionsCache = cache;
            state.optionsCacheValid = true;
        },
        invalidateOptionsCache(state) {
            state.optionsCacheValid = false;
        },
        setComments(state, comments) {
            state.comments = comments;
        }
    },
    getters: {
        filteredSales:
            (state) =>
                (excludeFilters = []) => {
                    // Pre-compute filter conditions that will be used
                    const filters = state.filters;
                    const excludeSet = new Set(excludeFilters);

                    // Pre-compute Sets for faster lookups (O(1) instead of O(n))
                    const clientsSet = !excludeSet.has("clients") && filters.clients?.length > 0
                        ? new Set(filters.clients) : null;
                    const chainsSet = !excludeSet.has("chains") && filters.chains?.length > 0
                        ? new Set(filters.chains) : null;
                    const shopsSet = !excludeSet.has("shops") && filters.shops?.length > 0
                        ? new Set(filters.shops) : null;
                    const salesmenSet = !excludeSet.has("salesmen") && filters.salesmen?.length > 0
                        ? new Set(filters.salesmen) : null;
                    const categoryIdsSet = !excludeSet.has("categories") && filters.categoryIds?.length > 0
                        ? new Set(filters.categoryIds) : null;
                    const brandIdsSet = !excludeSet.has("brands") && filters.brandIds?.length > 0
                        ? new Set(filters.brandIds) : null;
                    const netoWeightsSet = !excludeSet.has("netoWeights") && filters.netoWeights?.length > 0
                        ? new Set(filters.netoWeights) : null;
                    const countriesSet = !excludeSet.has("countries") && filters.countries?.length > 0
                        ? new Set(filters.countries) : null;
                    const infoProvidersSet = !excludeSet.has("infoProviders") && filters.infoProviders?.length > 0
                        ? new Set(filters.infoProviders) : null;
                    const productCodesSet = !excludeSet.has("products") && filters.productCodes?.length > 0
                        ? new Set(filters.productCodes) : null;
                    const shopTypesSet = !excludeSet.has("shopType") && filters.shopTypes?.length > 0
                        ? new Set(filters.shopTypes) : null;
                    const productTypesSet = !excludeSet.has("productTypes") && filters.productTypes?.length > 0
                        ? new Set(filters.productTypes) : null;

                    // Pre-compute regions search array
                    let searchRegionsSet = null;
                    if (!excludeSet.has("regions") && filters.regions?.length > 0) {
                        const regionsMap = {
                            ee: [0, 6],
                            eu: [1, 5, 7],
                            noneu: [2],
                        };
                        let searchRegions = [];
                        // Combine all selected regions
                        filters.regions.forEach((region) => {
                            if (regionsMap[region]) {
                                searchRegions = [...searchRegions, ...regionsMap[region]];
                            }
                        });
                        if (searchRegions.length > 0) {
                            searchRegionsSet = new Set(searchRegions);
                        }
                    }

                    // Single pass filter - check all conditions at once
                    return state.sales.filter((sale) => {
                        return (!clientsSet || clientsSet.has(sale.client_name)) &&
                            (!chainsSet || chainsSet.has(sale.chain_name)) &&
                            (!shopsSet || shopsSet.has(sale.client_shop_name)) &&
                            (!salesmenSet || salesmenSet.has(sale.salesman)) &&
                            (!categoryIdsSet || categoryIdsSet.has(sale.product_category_id)) &&
                            (!brandIdsSet || brandIdsSet.has(sale.product_brand_id)) &&
                            (!netoWeightsSet || netoWeightsSet.has(sale.product_neto_weight)) &&
                            (!countriesSet || countriesSet.has(sale.client_shop_country) || countriesSet.has(sale.country_et)) &&
                            (!infoProvidersSet || infoProvidersSet.has(sale.info_provider)) &&
                            (!productCodesSet || productCodesSet.has(sale.article_code)) &&
                            (!searchRegionsSet || searchRegionsSet.has(sale.export_flag)) &&
                            (!shopTypesSet || shopTypesSet.has(sale.client_shop_type)) &&
                            (!productTypesSet || productTypesSet.has((!sale.product_type || sale.product_type === "info_puudu") ? "unknown" : sale.product_type));
                    });
                },
        productOptions(state, getters) {
            // If cache is valid, return cached result
            if (state.optionsCacheValid) {
                return state.optionsCache.products;
            }

            // Build list of ALL unique products from state.sales
            const productMap = new Map();
            const products = [];

            // Build Set of active product codes (only when filtering is enabled)
            let activeProductSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.products;
                activeProductSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.article_code) {
                            activeProductSet.add(sale.article_code);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const productValue = sale.article_code;
                const productText = `[${sale.article_code}] ${sale.product_name}`;

                if (!productValue || !productText || productMap.has(productValue)) {
                    return;
                }

                products.push({
                    text: productText,
                    value: productValue,
                    // If filtering disabled, show all as active (dark). If enabled, check against calculatedSales
                    active: activeProductSet ? activeProductSet.has(productValue) : true,
                });

                productMap.set(productValue, true);
            });

            // Sort: active items first, then inactive
            return products.sort((a, b) => Number(b.active) - Number(a.active));
        },
        categoryOptions(state, getters) {
            if (state.optionsCacheValid) {
                return state.optionsCache.categories;
            }

            const categoryMap = new Map();
            const categories = [];

            // Build Set of active category IDs (only when filtering is enabled)
            let activeCategorySet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.categories;
                activeCategorySet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.product_category_id) {
                            activeCategorySet.add(sale.product_category_id);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const categoryValue = sale.product_category_id;
                const categoryText = sale.product_category;

                if (!categoryValue || !categoryText || categoryMap.has(categoryValue)) {
                    return;
                }

                categories.push({
                    text: categoryText,
                    value: categoryValue,
                    active: activeCategorySet ? activeCategorySet.has(categoryValue) : true,
                });

                categoryMap.set(categoryValue, true);
            });

            // Sort: active items first, then inactive
            return categories.sort((a, b) => Number(b.active) - Number(a.active));
        },
        productTypeOptions(state, getters) {
            if (state.optionsCacheValid) {
                return state.optionsCache.productTypes;
            }

            const productTypeMap = new Map();
            const productTypes = [];

            const productTypeLabels = {
                product: "Toode",
                raw_material: "Tooraine",
                other: "Muu",
                unknown: "Teadmata",
            };

            // Normalize product type value (handle null, undefined, "info_puudu")
            const normalizeProductType = (value) => {
                if (!value || value === "info_puudu") return "unknown";
                return value;
            };

            // Build Set of active product types (only when filtering is enabled)
            let activeProductTypeSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.productTypes;
                activeProductTypeSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        const productType = normalizeProductType(sale.product_type);
                        activeProductTypeSet.add(productType);
                    });
                }
            }

            state.sales.forEach((sale) => {
                const productTypeValue = normalizeProductType(sale.product_type);

                if (productTypeMap.has(productTypeValue)) {
                    return;
                }

                productTypes.push({
                    text: productTypeLabels[productTypeValue] || productTypeValue,
                    value: productTypeValue,
                    active: activeProductTypeSet ? activeProductTypeSet.has(productTypeValue) : true,
                });

                productTypeMap.set(productTypeValue, true);
            });

            // Sort: active items first, then inactive
            return productTypes.sort((a, b) => Number(b.active) - Number(a.active));
        },
        brandOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.brands;

            const brandMap = new Map();
            const brands = [];

            // Build Set of active brand IDs (only when filtering is enabled)
            let activeBrandSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.brands;
                activeBrandSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.product_brand_id) {
                            activeBrandSet.add(sale.product_brand_id);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const brandValue = sale.product_brand_id;
                const brandText = sale.product_brand;

                if (!brandValue || !brandText || brandMap.has(brandValue)) {
                    return;
                }

                brands.push({
                    text: brandText,
                    value: brandValue,
                    active: activeBrandSet ? activeBrandSet.has(brandValue) : true,
                });

                brandMap.set(brandValue, true);
            });

            // Sort: active items first, then inactive
            return brands.sort((a, b) => Number(b.active) - Number(a.active));
        },
        netoWeightOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.netoWeights;
            const weightMap = new Map();
            const weights = [];

            // Build Set of active weights (only when filtering is enabled)
            let activeWeightSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.netoWeights;
                activeWeightSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.product_neto_weight != null) {
                            activeWeightSet.add(sale.product_neto_weight);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const weightValue = sale.product_neto_weight;

                if (weightValue == null || weightMap.has(weightValue)) {
                    return;
                }

                weights.push({
                    text: `${weightValue} g`,
                    value: weightValue,
                    active: activeWeightSet ? activeWeightSet.has(weightValue) : true,
                });

                weightMap.set(weightValue, true);
            });

            // Sort: active items first, then inactive
            return weights.sort((a, b) => Number(b.active) - Number(a.active));
        },
        countryOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.countries;
            const countryMap = new Map();
            const countries = [];

            // Build Set of active countries (only when filtering is enabled)
            let activeCountrySet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.countries;
                activeCountrySet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.client_shop_country) {
                            activeCountrySet.add(sale.client_shop_country);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const countryValue = sale.client_shop_country;
                const countryText = sale.country_et || sale.client_shop_country;

                if (!countryValue || !countryText || countryMap.has(countryValue)) {
                    return;
                }

                countries.push({
                    text: countryText,
                    value: countryValue,
                    active: activeCountrySet ? activeCountrySet.has(countryValue) : true,
                });

                countryMap.set(countryValue, true);
            });

            // Sort: active items first, then inactive
            return countries.sort((a, b) => Number(b.active) - Number(a.active));
        },
        clientOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.clients;
            const clientMap = new Map();
            const clients = [];

            // Build Set of active clients (only when filtering is enabled)
            let activeClientSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.clients;
                activeClientSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.client_name) {
                            activeClientSet.add(sale.client_name);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const clientValue = sale.client_name;

                if (!clientValue || clientMap.has(clientValue)) {
                    return;
                }

                clients.push({
                    text: clientValue,
                    value: clientValue,
                    active: activeClientSet ? activeClientSet.has(clientValue) : true,
                });

                clientMap.set(clientValue, true);
            });

            // Sort: active items first, then inactive
            return clients.sort((a, b) => Number(b.active) - Number(a.active));
        },
        infoProviderOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.infoProviders;
            const providerMap = new Map();
            const providers = [];

            // Build Set of active providers (only when filtering is enabled)
            let activeProviderSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.infoProviders;
                activeProviderSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.info_provider) {
                            activeProviderSet.add(sale.info_provider);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const providerValue = sale.info_provider;

                if (!providerValue || providerMap.has(providerValue)) {
                    return;
                }

                providers.push({
                    text: providerValue,
                    value: providerValue,
                    active: activeProviderSet ? activeProviderSet.has(providerValue) : true,
                });

                providerMap.set(providerValue, true);
            });

            // Sort: active items first, then inactive
            return providers.sort((a, b) => Number(b.active) - Number(a.active));
        },
        chainOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.chains;
            const chainMap = new Map();
            const chains = [];

            // Build Set of active chains (only when filtering is enabled)
            let activeChainSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.chains;
                activeChainSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.chain_name) {
                            activeChainSet.add(sale.chain_name);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const chainValue = sale.chain_name;

                if (!chainValue || chainMap.has(chainValue)) {
                    return;
                }

                chains.push({
                    text: chainValue,
                    value: chainValue,
                    active: activeChainSet ? activeChainSet.has(chainValue) : true,
                });

                chainMap.set(chainValue, true);
            });

            // Sort: active items first, then inactive
            return chains.sort((a, b) => Number(b.active) - Number(a.active));
        },
        shopOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.shops;
            const shopsMap = new Map();
            const shops = [];

            // Build Set of active shops (only when filtering is enabled)
            let activeShopSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.shops;
                activeShopSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.client_shop_name) {
                            activeShopSet.add(sale.client_shop_name);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const shopValue = sale.client_shop_name;

                if (!shopValue || shopsMap.has(shopValue)) {
                    return;
                }

                shops.push({
                    text: shopValue,
                    value: shopValue,
                    active: activeShopSet ? activeShopSet.has(shopValue) : true,
                });

                shopsMap.set(shopValue, true);
            });

            // Sort: active items first, then inactive
            return shops.sort((a, b) => Number(b.active) - Number(a.active));
        },
        salesmanOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.salesmen;
            const salesmenMap = new Map();
            const salesmen = [];

            // Build Set of active salesmen (only when filtering is enabled)
            let activeSalesmanSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.salesmen;
                activeSalesmanSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.salesman) {
                            activeSalesmanSet.add(sale.salesman);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const salesmenValue = sale.salesman;

                if (!salesmenValue || salesmenMap.has(salesmenValue)) {
                    return;
                }

                salesmen.push({
                    text: salesmenValue,
                    value: salesmenValue,
                    active: activeSalesmanSet ? activeSalesmanSet.has(salesmenValue) : true,
                });

                salesmenMap.set(salesmenValue, true);
            });

            // Sort: active items first, then inactive
            return salesmen.sort((a, b) => Number(b.active) - Number(a.active));
        },
        shopTypeOptions(state, getters) {
            if (state.optionsCacheValid) return state.optionsCache.shopTypes;
            const shopTypesMap = new Map();
            const shopTypes = [];

            // Build Set of active shop types (only when filtering is enabled)
            let activeShopTypeSet = null;
            if (state.filterOptionsEnabled) {
                const activeSales = state.calculatedSales.shopType;
                activeShopTypeSet = new Set();
                if (activeSales && activeSales.length > 0) {
                    activeSales.forEach((sale) => {
                        if (sale.client_shop_type) {
                            activeShopTypeSet.add(sale.client_shop_type);
                        }
                    });
                }
            }

            state.sales.forEach((sale) => {
                const shopTypeValue = sale.client_shop_type;

                if (!shopTypeValue || shopTypesMap.has(shopTypeValue)) {
                    return;
                }

                shopTypes.push({
                    text: shopTypeValue.charAt(0).toUpperCase() + shopTypeValue.slice(1),
                    value: shopTypeValue,
                    active: activeShopTypeSet ? activeShopTypeSet.has(shopTypeValue) : true,
                });

                shopTypesMap.set(shopTypeValue, true);
            });

            // Sort: active items first, then inactive
            return shopTypes.sort((a, b) => Number(b.active) - Number(a.active));
        },
        xAxisValues(state) {
            const { start, end } = state.fetchDates;
            if (!start || !end) return [];
            const groupByWeek = state.groupByPeriod.includes("week");
            const startDate = new Date(start);
            const endDate = new Date(end);
            const dateArray = Array.from({ length: (endDate - startDate) / 86400000 + 1 }, (_, i) => {
                const d = new Date(startDate);
                d.setDate(d.getDate() + i);
                return d;
            });

            const uniquePeriods = new Set(
                dateArray.map((date) => {
                    const year = date.getFullYear();
                    if (groupByWeek) {
                        const tempDate = new Date(date);
                        tempDate.setUTCDate(tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7));
                        const firstDayOfYear = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
                        const week = Math.ceil(((tempDate - firstDayOfYear) / 86400000 + 1) / 7);
                        return `${year}-W${String(week).padStart(2, "0")}`;
                    } else {
                        return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                    }
                })
            );

            return Array.from(uniquePeriods).sort();
        },
    },
};
